/**
 * Wavelink-coordinated query keys.
 *
 * Unlike most react-query keys (which are private to the dashboard and can be renamed freely),
 * these key prefixes are part of a contract with the wavelink service. Wavelink pushes
 * invalidation messages keyed by these prefixes and advertises them as "covered" query keys so the
 * dashboard can suppress its own polling (see `isQueryCovered` in `useWavelink`). If you rename one
 * of these here, you MUST make the matching change in the wavelink repo, or live updates and
 * polling-suppression for that query will silently break.
 *
 * Always reference these constants when building the corresponding query key so the coordination
 * point stays discoverable and impossible to drift by accident.
 */

export const DEVICES_LIST_QUERY_KEY = 'devices-list';
export const HISTORICAL_SENSOR_DATA_QUERY_KEY = 'historical-sensor-data';
export const SUBSCRIPTION_TAGS_INFO_QUERY_KEY = 'subscription-tags-info';
export const SATELLITE_CREDITS_USAGE_HISTORY_QUERY_KEY = 'satellite-credits-usage-history';
export const STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY = 'stripe-current-customer-info';
export const DETECTIONS_QUERY_KEY = 'detections';
export const DEVICE_TRACK_HISTORY_QUERY_KEY = 'device-track-history';
export const DEVICE_STATUS_HISTORY_QUERY_KEY = 'device-status-history';
export const DEVICE_CELLULAR_SIGNAL_HISTORY_QUERY_KEY = 'device-cellular-signal-history';
export const HISTORICAL_BAROMETRIC_PRESSURE_DATA_QUERY_KEY = 'historical-barometric-pressure-data';
export const HISTORICAL_WIND_DATA_QUERY_KEY = 'historical-wind-data';
export const HISTORICAL_SURFACE_TEMPERATURE_DATA_QUERY_KEY = 'historical-surface-temperature-data';
export const HISTORICAL_WAVE_DATA_QUERY_KEY = 'historical-wave-data';
export const PARTITION_DATA_HISTORY_QUERY_KEY = 'partition-data-history';
export const OCEANS_WAVE_DATA_QUERY_KEY = 'oceans-wave-data';
export const OTA_READINESS_QUERY_KEY = 'device-ota-readiness';
export const OTA_PREVIEW_QUERY_KEY = 'device-ota-preview';
export const OTA_DETAIL_QUERY_KEY = 'device-ota-detail';

/**
 * Every prefix keyed through `dateRangeQueryKey`, and so every prefix `useLiveSplitQuery` accepts.
 *
 * Deliberately independent of wavelink coverage. Splitting a window that ends at `now` pays for itself
 * whether the live edge is refreshed by a push, by `refetchInterval`, or by nothing at all; coverage is an
 * optional extra that `isQueryCovered` answers at runtime.
 */
export const DATE_RANGE_QUERY_KEYS = [
    HISTORICAL_SENSOR_DATA_QUERY_KEY,
    DETECTIONS_QUERY_KEY,
    DEVICE_TRACK_HISTORY_QUERY_KEY,
    DEVICE_STATUS_HISTORY_QUERY_KEY,
    DEVICE_CELLULAR_SIGNAL_HISTORY_QUERY_KEY,
    HISTORICAL_BAROMETRIC_PRESSURE_DATA_QUERY_KEY,
    HISTORICAL_WIND_DATA_QUERY_KEY,
    HISTORICAL_SURFACE_TEMPERATURE_DATA_QUERY_KEY,
    HISTORICAL_WAVE_DATA_QUERY_KEY,
    PARTITION_DATA_HISTORY_QUERY_KEY,
    OCEANS_WAVE_DATA_QUERY_KEY,
] as const;

export type DateRangeQueryKeyPrefix = (typeof DATE_RANGE_QUERY_KEYS)[number];

/**
 * Prefixes shared with wavelink for push invalidation: all date-ranged keys plus `devices-list`.
 * `useWavelink` requires an invalidation handler for each. Uncovered prefixes fall back to interval
 * refreshes until wavelink advertises coverage.
 */
export type WavelinkQueryKeyPrefix = typeof DEVICES_LIST_QUERY_KEY | DateRangeQueryKeyPrefix;

/**
 * Date-ranged query keys.
 *
 * `useLiveSplitQuery` is the only thing that builds these, and the wavelink invalidation predicates
 * are the only thing that reads them back. Builder and parser live together here so the two can
 * never disagree about the shape: a predicate that inspects a segment by index silently matches
 * nothing if that segment moves, which would leave live charts stale with no error and no failing
 * test.
 *
 * Serialized `startDate`/`endDate` strings rather than Dates, so this module stays dependency free.
 */
export type DateRangeQueryParams = { startDate: string; endDate: string } | null;

export type DateRangeQueryOptions = {
    isPublic: boolean;
    /** Marks the query covering the live edge of a range that has been split. */
    live?: boolean;
} & Record<string, unknown>;

export const dateRangeQueryKey = (
    prefix: DateRangeQueryKeyPrefix,
    spotterId: string | null | undefined,
    dateRange: DateRangeQueryParams,
    options: DateRangeQueryOptions
): unknown[] => [prefix, spotterId, dateRange, options];

const isDateRangeQueryParams = (value: unknown): value is { startDate: string; endDate: string } => {
    return (
        typeof value === 'object' &&
        value !== null &&
        'startDate' in value &&
        'endDate' in value &&
        typeof value.startDate === 'string' &&
        typeof value.endDate === 'string'
    );
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export type ParsedDateRangeQueryKey = {
    prefix: string;
    spotterId: string;
    /** Null while the caller is still resolving a range. */
    dateRange: { startDate: string; endDate: string } | null;
    isLiveWindow: boolean;
    /**
     * The options segment as it was built. Exposed so a caller can tell two keys of the same series apart
     * from two unrelated keys without reading segments by index, which is the drift this module exists to
     * prevent.
     */
    options: Record<string, unknown>;
};

/**
 * Reads back a key produced by the builders above. Returns null when the key does not have that
 * shape, so callers can decide how to degrade rather than silently treating it as a non-match.
 */
export const parseDateRangeQueryKey = (queryKey: readonly unknown[]): ParsedDateRangeQueryKey | null => {
    if (queryKey.length < 4) {
        return null;
    }

    const [prefix, spotterId, dateRange, options] = queryKey;
    if (typeof prefix !== 'string' || typeof spotterId !== 'string') {
        return null;
    }
    if (!isRecord(options)) {
        return null;
    }
    if (dateRange !== null && !isDateRangeQueryParams(dateRange)) {
        return null;
    }

    return {
        prefix,
        spotterId,
        dateRange,
        isLiveWindow: options.live === true,
        options,
    };
};
